import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Checkbox,
  Radio,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FlexService from '../../services/FlexService';
import EntityWizardOverlay from './EntityWizardOverlay';
import StructuredSearchField from './StructuredSearchField';
import { formatDateOnly } from '../../utils/dateOnly';

/**
 * EntityPickerOverlay - Full-workspace overlay for selecting one or more records
 * from a loadScript result, with optional inline creation via wizard.
 *
 * JSON config (pickerConfig):
 * {
 *   "loadScript": "Receipt.List",
 *   "loadParams": { "receiptStatus": "OPEN,EXPECTED" },
 *   "primaryKey": "receiptId",
 *   "displayField": "receiptId",
 *   "columns": [{ field, label, type, width }],
 *   "multiSelect": true,
 *   "createAction": {
 *     "label": "New Receipt",
 *     "entityName": "Receipt",
 *     "detailEntityName": "ReceiptLine",
 *     "scriptName": "receipt_create_script",
 *     "title": "Create Receipt"
 *   },
 *   "submitScript": "trailer_assign_receipts",
 *   "submitPayloadMap": { "trailerId": "id", "trailerNumber": "trailerNumber" },
 *   "extraSearchFields": [{ "field": "itemId", "label": "Item" }],
 *   "extraPayload": { "mode": "orders" },
 *   "summaryScript": "wave_plan_summary",
 *   "summaryFields": [{ "field": "lineCount", "label": "Lines" }, { "field": "estimatedWeight", "label": "Est. Weight", "format": "decimal" }]
 * }
 */
const DEFAULT_SUMMARY_FIELDS = [
  { field: 'lineCount', label: 'Lines' },
  { field: 'totalQuantity', label: 'Qty' },
  { field: 'estimatedWeight', label: 'Est. Weight', format: 'decimal' },
  { field: 'estimatedPallets', label: 'Est. Pallets' },
];

const EntityPickerOverlay = ({ pickerConfig, parentRowData, colors, onSubmit, onClose }) => {
  const {
    loadScript,
    loadParams,
    primaryKey = 'id',
    displayField,
    columns = [],
    multiSelect = true,
    createAction,
    submitScript,
    submitPayloadMap,
    extraPayload,
    summaryScript,
    summaryFields,
    title: pickerTitle,
    showOnlyWhere,
    deferLoad = false,
    searchFields = [],
    extraSearchFields = [],
  } = pickerConfig;

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filterValues, setFilterValues] = useState({});
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  // Wizard state for inline creation
  const [wizardOpen, setWizardOpen] = useState(false);

  // Load picker data. Accepts an optional filter override so callers (e.g. the
  // Advanced Search panel's onSearch) can pass fresh values directly instead of
  // relying on `filterValues` state, which wouldn't have committed yet in the same tick.
  const loadData = useCallback(async (overrideFilters) => {
    if (!loadScript) return;
    setLoading(true);
    setHasSearched(true);
    try {
      const params = { ...(loadParams || {}) };

      // Search params (free-text `q` and/or field=value conditions) from StructuredSearchField
      const activeFilters = overrideFilters || filterValues;
      Object.entries(activeFilters).forEach(([key, val]) => {
        if (val && String(val).trim()) {
          params[key] = String(val).trim();
        }
      });

      const context = {};
      if (Object.keys(params).length > 0) {
        context._queryParams = params;
      }
      const response = await FlexService.executeScript({ scriptName: loadScript, context });
      if (!response.success) {
        setData([]);
        return;
      }
      const ctx = response.context || {};
      let result = ctx.result || ctx.data || ctx.response;
      if (!result) { setData([]); return; }

      // Unwrap to array
      let rows = null;
      if (Array.isArray(result)) { rows = result; }
      else {
        for (const key of ['data', 'items', 'list', 'rows', 'results']) {
          if (Array.isArray(result[key])) { rows = result[key]; break; }
        }
        if (!rows) {
          const entityPart = loadScript.split('.')[0].toLowerCase();
          if (Array.isArray(result[entityPart + 's'])) { rows = result[entityPart + 's']; }
        }
      }
      if (!rows) { setData([]); return; }

      // Apply client-side filter (e.g. showOnlyWhere: { "available": true })
      if (showOnlyWhere && typeof showOnlyWhere === 'object') {
        rows = rows.filter((row) =>
          Object.entries(showOnlyWhere).every(([field, value]) => row[field] === value)
        );
      }

      setData(rows);
    } catch (err) {
      console.error('Picker load failed:', err);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [loadScript, loadParams, filterValues]);

  // Auto-load on open unless deferLoad is true
  useEffect(() => {
    if (!deferLoad) { loadData(); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Field metadata for the search box's field-name suggestions (StructuredSearchField) —
  // same "click a field, type field=value" experience as the main grids. Prefers an
  // explicit searchFields config (picker declares its own richer set, e.g. dropdowns),
  // falling back to deriving one straight from the picker's own columns so every
  // picker gets this for free without extra JSON. extraSearchFields adds fields that
  // are searchable but not worth showing as a column (e.g. itemId on an Orders/
  // Shipments picker — an order can have many items, so it can't be a single-value
  // column, but "show me orders with item X" is still a useful filter).
  const structuredFieldMeta = useMemo(() => {
    const base = searchFields.length > 0
      ? searchFields.map((f) => ({
          fieldName: f.field,
          displayLabel: f.label || f.field,
          fieldType: f.type || 'TEXT',
        }))
      : columns.map((c) => ({
          fieldName: c.field,
          displayLabel: c.label || c.field,
          fieldType: c.type || 'TEXT',
        }));
    const extra = extraSearchFields
      .filter((f) => !base.some((b) => b.fieldName === f.field))
      .map((f) => ({
        fieldName: f.field,
        displayLabel: f.label || f.field,
        fieldType: f.type || 'TEXT',
      }));
    return [...base, ...extra];
  }, [searchFields, columns, extraSearchFields]);

  const handleAdvancedSearch = useCallback((params) => {
    setFilterValues(params);
    loadData(params);
  }, [loadData]);

  const handleAdvancedSearchClear = useCallback(() => {
    setFilterValues({});
    loadData({});
  }, [loadData]);

  // Client-side sort + per-column filter — applied to whatever page of data is
  // currently loaded. Every column is sortable/filterable by default (opt out
  // per-column with sortable:false / filterable:false in pickerConfig.columns).
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [columnFilters, setColumnFilters] = useState({});

  const handleSortClick = useCallback((col) => {
    if (col.sortable === false) return;
    if (sortColumn === col.field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(col.field);
      setSortDirection('asc');
    }
  }, [sortColumn]);

  const handleColumnFilterChange = useCallback((field, value) => {
    setColumnFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const displayData = useMemo(() => {
    let rows = data;

    const activeColumnFilters = Object.entries(columnFilters).filter(([, v]) => v && String(v).trim());
    if (activeColumnFilters.length > 0) {
      rows = rows.filter((row) =>
        activeColumnFilters.every(([field, val]) =>
          String(row[field] ?? '').toLowerCase().includes(String(val).toLowerCase())
        )
      );
    }

    if (sortColumn) {
      rows = [...rows].sort((a, b) => {
        const av = a[sortColumn];
        const bv = b[sortColumn];
        if (av == null && bv == null) return 0;
        if (av == null) return sortDirection === 'asc' ? -1 : 1;
        if (bv == null) return sortDirection === 'asc' ? 1 : -1;
        if (typeof av === 'number' && typeof bv === 'number') {
          return sortDirection === 'asc' ? av - bv : bv - av;
        }
        const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sortDirection === 'asc' ? cmp : -cmp;
      });
    }

    return rows;
  }, [data, columnFilters, sortColumn, sortDirection]);

  // Toggle selection
  const toggleSelect = useCallback((id) => {
    setSelectedIds((prev) => {
      if (!multiSelect) return prev.includes(id) ? [] : [id];
      return prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
    });
  }, [multiSelect]);

  // Select all / none
  const toggleAll = useCallback(() => {
    if (selectedIds.length === displayData.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(displayData.map((row) => String(row[primaryKey])));
    }
  }, [selectedIds, displayData, primaryKey]);

  // Build the payload shared by the submit call and the live summary call:
  // parent row fields (submitPayloadMap) + any static extras + the selected keys.
  const buildSelectionPayload = useCallback((ids) => {
    const payload = { ...(extraPayload || {}) };
    if (submitPayloadMap && parentRowData) {
      Object.entries(submitPayloadMap).forEach(([payloadKey, rowField]) => {
        payload[payloadKey] = parentRowData[rowField];
      });
    }
    const selectedKey = pickerConfig.submitSelectedKey || 'selectedIds';
    payload[selectedKey] = multiSelect ? ids : ids[0];
    return payload;
  }, [extraPayload, submitPayloadMap, parentRowData, multiSelect, pickerConfig.submitSelectedKey]);

  // Live selection summary — debounced call to summaryScript whenever the
  // selection changes, so the user can see totals (lines, weight, etc.)
  // before committing. Only active when the picker config declares one.
  useEffect(() => {
    if (!summaryScript) return;
    if (selectedIds.length === 0) {
      setSummaryData(null);
      return;
    }
    setSummaryLoading(true);
    const timer = setTimeout(() => {
      const context = { _payload: buildSelectionPayload(selectedIds) };
      FlexService.executeScript({ scriptName: summaryScript, context })
        .then((resp) => {
          const result = resp?.context?.result || resp?.context?.data || null;
          setSummaryData(result);
        })
        .catch((err) => {
          console.error('Picker summary failed:', err);
          setSummaryData(null);
        })
        .finally(() => setSummaryLoading(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [selectedIds, summaryScript, buildSelectionPayload]);

  // Submit selected items
  const handleSubmit = useCallback(async () => {
    if (selectedIds.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      if (submitScript) {
        const context = { _payload: buildSelectionPayload(selectedIds) };
        await FlexService.executeScript({ scriptName: submitScript, context });
        if (onSubmit) onSubmit(selectedIds);
      } else {
        // No submitScript — return selected row data directly (used by inline field pickers)
        const selectedRows = data.filter((row) => selectedIds.includes(String(row[primaryKey])));
        if (onSubmit) onSubmit(selectedRows);
      }
    } catch (err) {
      console.error('Picker submit failed:', err);
      setError(err.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  }, [selectedIds, submitScript, buildSelectionPayload, onSubmit, data, primaryKey]);

  // Wizard create callback — reload data after creating a new record
  const handleWizardSave = useCallback((result) => {
    setWizardOpen(false);
    loadData();
    // If the created record has the primaryKey, auto-select it
    if (result && result[primaryKey]) {
      setSelectedIds((prev) => [...prev, String(result[primaryKey])]);
    }
  }, [loadData, primaryKey]);

  // Column type mapping
  const formatValue = (value, type) => {
    if (value === null || value === undefined) return '—';
    if (type === 'DATE' && value) {
      return formatDateOnly(value);
    }
    if (type === 'BOOLEAN') return value ? 'Yes' : 'No';
    return String(value);
  };

  const inputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 0,
      bgcolor: colors.background,
      '& fieldset': { borderColor: colors.border },
      '&:hover fieldset': { borderColor: '#22c55e' },
      '&.Mui-focused fieldset': { borderColor: '#22c55e' },
    },
    '& .MuiInputBase-input': { color: colors.textPrimary, fontSize: '0.8rem', py: 0.75 },
  };

  // If wizard is open, render it full-screen on top
  if (wizardOpen && createAction) {
    return (
      <EntityWizardOverlay
        entityName={createAction.entityName}
        detailEntityName={createAction.detailEntityName || null}
        wizardKey={createAction.wizardKey || null}
        rowData={null}
        scriptName={createAction.scriptName}
        title={createAction.title || 'Create New'}
        saveLabel={createAction.saveLabel || 'Create'}
        colors={colors}
        onSave={handleWizardSave}
        onClose={() => setWizardOpen(false)}
      />
    );
  }

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: colors.background,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: colors.surface,
        }}
      >
        <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 600, flex: 1 }}>
          {pickerTitle || 'Select Records'}
        </Typography>
        {selectedIds.length > 0 && (
          <Chip
            label={`${selectedIds.length} selected`}
            size="small"
            sx={{ bgcolor: 'rgba(34,197,94,0.15)', color: '#22c55e', borderRadius: 0, fontSize: '0.75rem' }}
          />
        )}
        <IconButton size="small" onClick={onClose} sx={{ color: colors.textSecondary }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Toolbar: search + create button */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          px: 2,
          py: 0.75,
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: colors.surface,
          position: 'relative',
        }}
      >
        {/* Search row — same field-suggestion structured search box as the main grids */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <StructuredSearchField
            fieldMetadata={structuredFieldMeta}
            onSearch={handleAdvancedSearch}
            onClear={handleAdvancedSearchClear}
            colors={colors}
            placeholder="Search... (type field=value for structured search)"
          />
          <Box sx={{ flex: 1 }} />
          {createAction && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => setWizardOpen(true)}
              sx={{
                borderRadius: 0,
                textTransform: 'none',
                fontSize: '0.75rem',
                borderColor: '#22c55e',
                color: '#22c55e',
                '&:hover': { borderColor: '#16a34a', bgcolor: 'rgba(34,197,94,0.08)' },
              }}
            >
              {createAction.label || 'Create New'}
            </Button>
          )}
        </Box>
      </Box>

      {/* Grid */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={28} sx={{ color: '#22c55e' }} />
          </Box>
        ) : !hasSearched && deferLoad ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 1 }}>
            <SearchIcon sx={{ fontSize: 40, color: colors.textMuted, opacity: 0.4 }} />
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              Enter search criteria and click Search
            </Typography>
          </Box>
        ) : displayData.length === 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6 }}>
            <Typography variant="body2" sx={{ color: colors.textMuted }}>
              No records found
            </Typography>
          </Box>
        ) : (
          <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', mt: 0.5 }}>
            <Box component="thead">
              <Box component="tr" sx={{ borderBottom: `1px solid ${colors.border}` }}>
                <Box
                  component="th"
                  sx={{ p: 0.5, width: 36, textAlign: 'center' }}
                >
                  {multiSelect && (
                    <Checkbox
                      size="small"
                      checked={selectedIds.length === displayData.length && displayData.length > 0}
                      indeterminate={selectedIds.length > 0 && selectedIds.length < displayData.length}
                      onChange={toggleAll}
                      sx={{ color: colors.textMuted, '&.Mui-checked': { color: '#22c55e' }, p: 0 }}
                    />
                  )}
                </Box>
                {columns.map((col) => {
                  const isSortable = col.sortable !== false;
                  return (
                    <Box
                      key={col.field}
                      component="th"
                      onClick={() => handleSortClick(col)}
                      sx={{
                        p: '4px 8px',
                        textAlign: 'left',
                        color: sortColumn === col.field ? colors.textPrimary : colors.textSecondary,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        cursor: isSortable ? 'pointer' : 'default',
                        userSelect: 'none',
                        whiteSpace: 'nowrap',
                        '&:hover': isSortable ? { color: colors.textPrimary } : {},
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                        {col.label}
                        {isSortable && sortColumn === col.field && (
                          sortDirection === 'asc'
                            ? <ArrowUpwardIcon sx={{ fontSize: 12 }} />
                            : <ArrowDownwardIcon sx={{ fontSize: 12 }} />
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
              <Box component="tr" sx={{ borderBottom: `1px solid ${colors.border}` }}>
                <Box component="th" sx={{ p: 0.25 }} />
                {columns.map((col) => (
                  <Box key={col.field} component="th" sx={{ p: '2px 6px' }}>
                    {col.filterable !== false && (
                      <TextField
                        size="small"
                        variant="standard"
                        placeholder="Filter..."
                        value={columnFilters[col.field] || ''}
                        onChange={(e) => handleColumnFilterChange(col.field, e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        InputProps={{ disableUnderline: false }}
                        sx={{
                          width: '100%',
                          '& .MuiInput-input': { fontSize: '0.7rem', color: colors.textPrimary, py: 0.25 },
                          '& .MuiInput-underline:before': { borderBottomColor: colors.border },
                        }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {displayData.map((row) => {
                const rowId = String(row[primaryKey]);
                const isSelected = selectedIds.includes(rowId);
                return (
                  <Box
                    key={rowId}
                    component="tr"
                    onClick={() => toggleSelect(rowId)}
                    sx={{
                      cursor: 'pointer',
                      borderBottom: `1px solid ${colors.border}`,
                      bgcolor: isSelected ? 'rgba(34,197,94,0.08)' : 'transparent',
                      '&:hover': { bgcolor: isSelected ? 'rgba(34,197,94,0.12)' : colors.surfaceElevated },
                    }}
                  >
                    <Box component="td" sx={{ p: 0.5, width: 36, textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                      {multiSelect ? (
                        <Checkbox
                          size="small"
                          checked={isSelected}
                          onChange={() => toggleSelect(rowId)}
                          sx={{ color: colors.textMuted, '&.Mui-checked': { color: '#22c55e' }, p: 0 }}
                        />
                      ) : (
                        // Single-select pickers (e.g. dock door assignment) already only ever
                        // keep one id selected (toggleSelect replaces the array), but a Checkbox
                        // visually implies multiple can stay checked at once -- Radio communicates
                        // the real constraint correctly.
                        <Radio
                          size="small"
                          checked={isSelected}
                          onChange={() => toggleSelect(rowId)}
                          sx={{ color: colors.textMuted, '&.Mui-checked': { color: '#22c55e' }, p: 0 }}
                        />
                      )}
                    </Box>
                    {columns.map((col) => (
                      <Box
                        key={col.field}
                        component="td"
                        sx={{
                          p: '4px 8px',
                          color: colors.textPrimary,
                          fontSize: '0.8rem',
                        }}
                      >
                        {col.type === 'STATUS' && col.statusMapping
                          ? (() => {
                              const val = row[col.field];
                              const mapping = col.statusMapping[val];
                              if (!mapping) return String(val || '—');
                              return (
                                <Chip
                                  label={mapping.label}
                                  size="small"
                                  sx={{
                                    borderRadius: 0,
                                    fontSize: '0.7rem',
                                    height: 20,
                                    bgcolor: `${mapping.color === 'blue' ? 'rgba(59,130,246,0.15)' : mapping.color === 'orange' ? 'rgba(245,158,11,0.15)' : mapping.color === 'green' ? 'rgba(34,197,94,0.15)' : mapping.color === 'gray' ? 'rgba(156,163,175,0.15)' : 'rgba(156,163,175,0.15)'}`,
                                    color: `${mapping.color === 'blue' ? '#3b82f6' : mapping.color === 'orange' ? '#f59e0b' : mapping.color === 'green' ? '#22c55e' : mapping.color === 'gray' ? '#9ca3af' : '#9ca3af'}`,
                                  }}
                                />
                              );
                            })()
                          : formatValue(row[col.field], col.type)}
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>

      {/* Live selection summary — totals for what's currently checked, so the
          user knows how much they're about to commit before submitting. */}
      {summaryScript && selectedIds.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 1,
            px: 2,
            py: 0.75,
            borderTop: `1px solid ${colors.border}`,
            bgcolor: colors.surfaceElevated,
          }}
        >
          {summaryLoading ? (
            <CircularProgress size={14} sx={{ color: '#22c55e' }} />
          ) : summaryData ? (
            (summaryFields || DEFAULT_SUMMARY_FIELDS).map((f) => {
              const raw = summaryData[f.field];
              if (raw === undefined || raw === null) return null;
              const value = f.format === 'decimal' ? Number(raw).toFixed(1) : raw;
              return (
                <Chip
                  key={f.field}
                  label={`${f.label}: ${value}`}
                  size="small"
                  sx={{ bgcolor: colors.background, color: colors.textPrimary, borderRadius: 0, fontSize: '0.72rem', border: `1px solid ${colors.border}` }}
                />
              );
            })
          ) : null}
        </Box>
      )}

      {/* Footer */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderTop: `1px solid ${colors.border}`,
          bgcolor: colors.surface,
        }}
      >
        {error && (
          <Typography variant="caption" sx={{ color: '#ef4444', flex: 1 }}>
            {error}
          </Typography>
        )}
        <Box sx={{ flex: error ? 0 : 1 }} />
        <Typography variant="caption" sx={{ color: colors.textMuted, mr: 1 }}>
          {displayData.length === data.length
            ? `${data.length} records`
            : `${displayData.length} of ${data.length} records`}
        </Typography>
        <Button
          size="small"
          onClick={onClose}
          sx={{ borderRadius: 0, color: colors.textSecondary, textTransform: 'none', fontSize: '0.8rem' }}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          disabled={selectedIds.length === 0 || submitting}
          startIcon={submitting ? <CircularProgress size={14} /> : <CheckCircleIcon sx={{ fontSize: 16 }} />}
          onClick={handleSubmit}
          sx={{
            borderRadius: 0,
            bgcolor: '#22c55e',
            textTransform: 'none',
            fontSize: '0.8rem',
            '&:hover': { bgcolor: '#16a34a' },
            '&.Mui-disabled': { bgcolor: colors.surfaceElevated },
          }}
        >
          {submitting ? 'Selecting...' : `${submitScript ? 'Assign' : 'Select'} ${selectedIds.length > 0 ? `(${selectedIds.length})` : ''}`}
        </Button>
      </Box>
    </Box>
  );
};

export default EntityPickerOverlay;
